import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { EAbaPrePrestador } from '@/features/servicos/shared/enums/abas-servicos.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';

import type { PrePrestadorDTO, PrePrestadorRequest } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/pre-prestador.model';
import type { AreasAtuacaoDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/areas-atuacao.model';
import type { AtividadesDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/atividades.model';
import type { CorpoClinicoDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/corpo-clinico.model';
import type { CorpoSocietarioDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/corpo-societario.model';
import type { EspecialidadesDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/especialidades.model';
import type { RecursosHumanosDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/recursos-humanos.model';
import type { RecursosMateriaisDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/recursos-materiais.model';
import type { ServicosDTO } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/servicos.model';
import { PrePrestadorServicosService } from '@/features/servicos/pessoa-juridica/content/pre-prestador-servicos/pre-prestador-servicos.service';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface ProtocoloResumoInput {
    portal: string;
    sas?: string;
    numeroProtocolo?: string;
    anoProtocolo?: string;
    ufCrm?: string;
}

interface OpcaoSelecao {
    label: string;
    value: string;
}

interface AbaSelecao {
    name: string;
    value: EAbaPrePrestador;
}

interface AnexoPrePrestador {
    chave: string;
    titulo: string;
    linkAnexo: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-pre-prestador-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, FormsModule, CardContent, DialogModule, VisualizadorPdf, InputText, InputMask, Select, SelectButton, Button, CanDirective],
    templateUrl: './pre-prestador-servicos.html',
    styleUrl: './pre-prestador-servicos.scss'
})
export class PrePrestadorServicos implements OnInit {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;

    protected readonly EAbaPrePrestador = EAbaPrePrestador;
    readonly protocoloSelecionado = input<ProtocoloResumoInput | null>(null);
    readonly codigoEstado = input('');
    readonly abaSelecionadaExterna = input<EAbaPrePrestador | null>(null);

    readonly abas: AbaSelecao[] = [
        { name: 'Dados', value: EAbaPrePrestador.DADOS },
        { name: 'Funcionamento', value: EAbaPrePrestador.FUNC },
        { name: 'Documentos', value: EAbaPrePrestador.DOC },
        { name: 'Corpo Societ\u00e1rio', value: EAbaPrePrestador.SOCIETARIO },
        { name: 'Corpo Cl\u00ednico', value: EAbaPrePrestador.CLINICO },
        { name: 'Especialidades', value: EAbaPrePrestador.ESPECIALIDADES },
        { name: '\u00c1rea de Atua\u00e7\u00e3o', value: EAbaPrePrestador.AREA_ATUACAO },
        { name: 'Servi\u00e7os', value: EAbaPrePrestador.SERVICOS },
        { name: 'Atividades', value: EAbaPrePrestador.ATIVIDADES },
        { name: 'Rec.Materiais', value: EAbaPrePrestador.REC_MATERIAIS },
        { name: 'Rec.Humanos', value: EAbaPrePrestador.REC_HUMANOS },
        { name: 'Documenta\u00e7\u00e3o Pendente', value: EAbaPrePrestador.PENDENTE },
        { name: 'Descontos', value: EAbaPrePrestador.DESCONTOS },
        { name: 'Anexos', value: EAbaPrePrestador.ANEXOS }
    ];
    readonly abaSelecionada = signal<EAbaPrePrestador>(EAbaPrePrestador.DADOS);
    readonly corpoSocietario = signal<CorpoSocietarioDTO[]>([]);
    readonly corpoClinico = signal<CorpoClinicoDTO[]>([]);
    readonly especialidades = signal<EspecialidadesDTO[]>([]);
    readonly areasAtuacao = signal<AreasAtuacaoDTO[]>([]);
    readonly servicosPrestador = signal<ServicosDTO[]>([]);
    readonly atividades = signal<AtividadesDTO[]>([]);
    readonly recursosMateriais = signal<RecursosMateriaisDTO[]>([]);
    readonly recursosHumanos = signal<RecursosHumanosDTO[]>([]);
    readonly documentosPendentes = signal<string[]>([]);
    readonly descontoMotivo = signal('');
    readonly anexosDisponiveis = signal<AnexoPrePrestador[]>([]);
    readonly isAnuidadePaga = signal<boolean | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-pre-prestador.pdf');
    readonly editando = signal(false);

    /**
     * Abas cujo conteúdo esta tela altera. As demais são listas que vêm do portal (corpo societário,
     * especialidades, anexos…): aparecem aqui para consulta, e não há o que salvar nelas.
     */
    private static readonly ABAS_EDITAVEIS: ReadonlySet<EAbaPrePrestador> = new Set([EAbaPrePrestador.DADOS, EAbaPrePrestador.FUNC, EAbaPrePrestador.DOC]);

    /** A aba aberta aceita edição? É o que habilita "Editar" no menu de ações. */
    readonly abaEditavel = computed(() => PrePrestadorServicos.ABAS_EDITAVEIS.has(this.abaSelecionada()));

    /**
     * Ações do formulário, agrupadas num menu só — o mesmo arranjo do "Dados de Registro" em médicos.
     *
     * Fora das abas editáveis o item fica DESABILITADO em vez de sumir: botão que desaparece ao trocar
     * de aba se lê como defeito, enquanto o item apagado diz que a ação existe e só não vale ali.
     *
     * `computed`, e não um array fixo: `disabled` é uma propriedade do item, então a lista precisa ser
     * remontada quando a aba muda — array fixo congelaria o estado da primeira aba aberta.
     */
    readonly acoesFormulario = computed<MenuItem[]>(() => [{ label: 'Editar', icon: 'pi pi-pencil', disabled: !this.abaEditavel(), command: () => this.habilitarEdicao() }]);
    readonly jsonAtualizacao = signal('');
    protected readonly rolePrePrestadorEditar = PERMISSOES.pessoaJuridica.prePrestador.editar;

    readonly yesNoOptions: OpcaoSelecao[] = [
        { label: 'Sim', value: 'S' },
        { label: 'N\u00e3o', value: 'N' }
    ];

    readonly ufs: OpcaoSelecao[] = [
        { label: 'AC', value: 'AC' },
        { label: 'AL', value: 'AL' },
        { label: 'AP', value: 'AP' },
        { label: 'AM', value: 'AM' },
        { label: 'BA', value: 'BA' },
        { label: 'CE', value: 'CE' },
        { label: 'DF', value: 'DF' },
        { label: 'ES', value: 'ES' },
        { label: 'GO', value: 'GO' },
        { label: 'MA', value: 'MA' },
        { label: 'MT', value: 'MT' },
        { label: 'MS', value: 'MS' },
        { label: 'MG', value: 'MG' },
        { label: 'PA', value: 'PA' },
        { label: 'PB', value: 'PB' },
        { label: 'PR', value: 'PR' },
        { label: 'PE', value: 'PE' },
        { label: 'PI', value: 'PI' },
        { label: 'RJ', value: 'RJ' },
        { label: 'RN', value: 'RN' },
        { label: 'RS', value: 'RS' },
        { label: 'RO', value: 'RO' },
        { label: 'RR', value: 'RR' },
        { label: 'SC', value: 'SC' },
        { label: 'SP', value: 'SP' },
        { label: 'SE', value: 'SE' },
        { label: 'TO', value: 'TO' }
    ];

    instalacoesOptions: OpcaoSelecao[] = [];
    naturezaTipoOptions: OpcaoSelecao[] = [];
    naturezaEsferaOptions: OpcaoSelecao[] = [];
    modalidadeOptions: OpcaoSelecao[] = [
        { label: 'Cadastro', value: 'C' },
        { label: 'Registro', value: 'R' }
    ];
    categoriaOptions: OpcaoSelecao[] = [
        { label: 'Civil', value: 'C' },
        { label: 'Civil Ltda.', value: 'L' },
        { label: 'Civil S/A', value: 'S' },
        { label: 'Militar', value: 'M' },
        { label: 'Eireli', value: 'E' }
    ];
    tipoOrganizacaoOptions: OpcaoSelecao[] = [
        { label: '\u00danico', value: 'U' },
        { label: 'Matriz', value: 'M' },
        { label: 'Filial sem Capital Social', value: 'S' },
        { label: 'Filial com Capital Social', value: 'C' },
        { label: 'Filial com matriz em outra UF', value: 'O' }
    ];
    licencaSanitariaOptions: OpcaoSelecao[] = [
        { label: 'Municipal', value: 'M' },
        { label: 'Estadual', value: 'E' },
        { label: 'Isento', value: 'I' }
    ];
    localRegistroOptions: OpcaoSelecao[] = [
        { label: 'Junta Comercial', value: 'J' },
        { label: 'Cart\u00f3rio', value: 'C' }
    ];
    tipoGestaoOptions: OpcaoSelecao[] = [
        { label: 'OS', value: 'O' },
        { label: 'OSCIP', value: 'C' },
        { label: 'P\u00fablico', value: 'P' },
        { label: 'Privado', value: 'R' }
    ];
    abrangenciaServicoOptions: OpcaoSelecao[] = [
        { label: 'Nacional', value: 'N' },
        { label: 'Estadual', value: 'E' },
        { label: 'Municipal', value: 'M' },
        { label: 'Intermunicipal - Microregional', value: 'I' },
        { label: 'Intermunicipal - Macroregional', value: 'A' },
        { label: 'Interestadual - Fronteiras', value: 'F' }
    ];
    condicaoFuncionamentoOptions: OpcaoSelecao[] = [
        { label: 'Funcionando', value: 'F' },
        { label: 'Filial de Empresa', value: 'L' },
        { label: 'Nova', value: 'N' },
        { label: 'Ambulat\u00f3rio Patronal', value: 'P' }
    ];

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';
    private dadosOriginais: PrePrestadorDTO | null = null;

    private readonly camposAnexos: ReadonlyArray<{ chave: keyof PrePrestadorDTO; titulo: string }> = [
        { chave: 'anexoDocumentoConstituicao', titulo: 'Documento de Constituição' },
        { chave: 'anexoLicencaSanitaria', titulo: 'Licença Sanitária' },
        { chave: 'anexoAlvaraFuncionamento', titulo: 'Alvará de Funcionamento' },
        { chave: 'anexoResponsabilidadeTecnica', titulo: 'Responsabilidade Técnica' },
        { chave: 'anexoRegularidadeFiscal', titulo: 'Regularidade Fiscal' },
        { chave: 'anexoComprovanteRegistroOCE', titulo: 'Comprovante Registro OCE' },
        { chave: 'anexoOutros', titulo: 'Outros' }
    ];

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.nonNullable.group(this.obterValoresIniciaisFormulario());
    private readonly prePrestadorServicosService = inject(PrePrestadorServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        effect(() => {
            this.abaSelecionadaExterna();
            this.sincronizarAbaExterna();
        });
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarPrePrestador();
        });
    }

    ngOnInit(): void {
        this.form.disable({ emitEvent: false });
    }

    private carregarPrePrestador(): void {
        const request = this.montarRequest();

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.form.reset(this.obterValoresIniciaisFormulario());
            this.form.disable({ emitEvent: false });
            this.editando.set(false);
            this.jsonAtualizacao.set('');
            this.resetarOpcoesDinamicas();
            this.resetarColecoes();
            this.documentosPendentes.set([]);
            this.descontoMotivo.set('');
            this.anexosDisponiveis.set([]);
            this.isAnuidadePaga.set(null);
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;

        if (this.ultimaChaveConsulta === chaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;

        this.prePrestadorServicosService
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (this.ultimaChaveConsulta !== chaveConsulta) {
                        return;
                    }

                    this.preencherFormulario(response);
                },
                error: () => {
                    if (this.ultimaChaveConsulta !== chaveConsulta) {
                        return;
                    }

                    this.form.reset(this.obterValoresIniciaisFormulario());
                    this.form.disable({ emitEvent: false });
                    this.editando.set(false);
                    this.jsonAtualizacao.set('');
                    this.resetarOpcoesDinamicas();
                    this.resetarColecoes();
                    this.documentosPendentes.set([]);
                    this.descontoMotivo.set('');
                    this.anexosDisponiveis.set([]);
                    this.isAnuidadePaga.set(null);
                    this.dadosOriginais = null;
                }
            });
    }

    private preencherFormulario(response: PrePrestadorDTO): void {
        this.resetarOpcoesDinamicas();
        this.dadosOriginais = response;
        const dados = response as Record<string, unknown>;

        this.corpoSocietario.set(this.normalizarLista(this.obterValor<CorpoSocietarioDTO[]>(dados, ['corpoSocietario', 'CORPO_SOCIETARIO'])));
        this.corpoClinico.set(this.normalizarLista(this.obterValor<CorpoClinicoDTO[]>(dados, ['corpoClinico', 'CORPO_CLINICO'])));
        this.especialidades.set(this.normalizarLista(this.obterValor<EspecialidadesDTO[]>(dados, ['especialidades', 'ESPECIALIDADES'])));
        this.areasAtuacao.set(this.normalizarLista(this.obterValor<AreasAtuacaoDTO[]>(dados, ['areasAtuacao', 'AREA_ATUACAO'])));
        this.servicosPrestador.set(this.normalizarLista(this.obterValor<ServicosDTO[]>(dados, ['servicos', 'SERVICOS'])));
        this.atividades.set(this.normalizarLista(this.obterValor<AtividadesDTO[]>(dados, ['atividades', 'ATIVIDADES'])));
        this.recursosMateriais.set(this.normalizarLista(this.obterValor<RecursosMateriaisDTO[]>(dados, ['recursosMateriais', 'RECURSOS_MATERIAIS'])));
        this.recursosHumanos.set(this.normalizarLista(this.obterValor<RecursosHumanosDTO[]>(dados, ['recursosHumanos', 'RECURSOS_HUMANOS'])));
        this.documentosPendentes.set((this.obterValor<unknown[]>(dados, ['documentosPendentes', 'DOCUMENTOS_PENDENTES']) ?? []).map((documento) => this.valorTexto(documento).trim()).filter(Boolean));
        this.descontoMotivo.set(this.normalizarDescontoMotivo(this.obterValor(dados, ['txDescontoMotivo', 'TX_DESCONTO_MOTIVO'])));
        this.isAnuidadePaga.set(this.normalizarBoolean(this.obterValor(dados, ['isAnuidadePaga', 'IS_ANUIDADE_PAGA'])));
        this.atualizarAnexos(response);

        const classificacaoLabel = this.valorTexto(this.obterValor(dados, ['classificacao.descricao', 'classificacaoDescricao', 'classificacaodescricao', 'CLASSIFICACAO_DESCRICAO']));
        const naturezaTipo = this.valorTexto(this.obterValor(dados, ['IN_NATUREZA_EMPRESA', 'naturezaJuridica']));
        const naturezaEsfera = this.valorTexto(this.obterValor(dados, ['IN_NAT_JURIDICA_MANTENEDOR', 'tipoMantenedora.id', 'tipoMantenedora.descricao']));
        const modalidade = this.valorTexto(this.obterValor(dados, ['IN_REG_CAD', 'modalidadeInscricao']));
        const categoria = this.valorTexto(this.obterValor(dados, ['IN_CATEGORIA', 'categoria']));
        const tipoOrganizacao = this.valorTexto(this.obterValor(dados, ['IN_TIPO_ESTABELECIMENTO', 'tipoEstabelecimento']));
        const instalacao = this.valorTexto(this.obterValor(dados, ['tipoInstalacao.id', 'tipoInstalacao.descricao', 'IN_INSTALACOES']));
        const licencaSanitaria = this.valorTexto(this.obterValor(dados, ['TIPO_LICENCA_SANITARIA', 'tipoLicencaSanitaria']));
        const localRegistro = this.normalizarLocalRegistro(this.obterValor(dados, ['IN_LOCAL_REGISTRO', 'localRegistro', 'LOCAL_REGISTRO']));
        const isJuntaComercial = this.ehJuntaComercial(localRegistro);
        const numeroNire = this.valorTexto(this.obterValor(dados, ['numeroNire', 'NUMERO_NIRE']));
        const dataDocumentoConstituicao = this.formatarData(this.valorTexto(this.obterValor(dados, ['dataDocumentoConstituicao', 'DATA_DOCUMENTO_CONSTITUICAO'])));
        const tipoDocumentoDescricao = this.valorTexto(this.obterValor(dados, ['tipoDocumento.descricao', 'tipoDocumentoDescricao', 'TIPO_DOCUMENTO_DESCRICAO']));
        const numeroRegistroDocumento = this.valorTexto(this.obterValor(dados, ['numeroRegistroDocumento', 'NUMERO_REGISTRO_DOCUMENTO', 'NU_DOCUMENTO_CONSTITUICAO']));
        const tipoGestao = this.valorTexto(this.obterValor(dados, ['TIPO_GESTAO', 'tipoGestao']));
        const abrangenciaServico = this.valorTexto(this.obterValor(dados, ['IN_ABRANGENCIA_SERVICO', 'abrangenciaServico']));
        const condicaoFuncionamento = this.valorTexto(this.obterValor(dados, ['IN_CONDICAO_FUNCIONAMENTO', 'condicaoFuncionamento']));
        const naturezaMantenedora = this.valorTexto(this.obterValor(dados, ['IN_NAT_JURIDICA_MANTENEDOR', 'naturezaJuridicaMantenedora']));

        this.adicionarOpcao(this.naturezaTipoOptions, naturezaTipo);
        this.adicionarOpcao(this.naturezaEsferaOptions, naturezaEsfera, response.tipoMantenedora?.descricao);
        this.adicionarOpcao(this.naturezaEsferaOptions, naturezaMantenedora);
        this.adicionarOpcao(this.modalidadeOptions, modalidade);
        this.adicionarOpcao(this.categoriaOptions, categoria);
        this.adicionarOpcao(this.tipoOrganizacaoOptions, tipoOrganizacao);
        this.adicionarOpcao(this.instalacoesOptions, instalacao, response.tipoInstalacao?.descricao);
        this.adicionarOpcao(this.licencaSanitariaOptions, licencaSanitaria);
        this.adicionarOpcao(this.localRegistroOptions, localRegistro);
        this.adicionarOpcao(this.tipoGestaoOptions, tipoGestao);
        this.adicionarOpcao(this.abrangenciaServicoOptions, abrangenciaServico);
        this.adicionarOpcao(this.condicaoFuncionamentoOptions, condicaoFuncionamento);

        this.form.patchValue(
            {
                diretorNome: this.valorTexto(this.obterValor(dados, ['nome', 'NOME'])),
                diretorCrm: this.valorTexto(this.obterValor(dados, ['numeroCrmDt', 'NUMERO_CRMDT', 'CRM_DT'])),
                diretorUf: this.valorTexto(this.obterValor(dados, ['ufCrmDt', 'UF_CRMDT', 'UF_CRM_DT'])),
                diretorCpf: this.valorTexto(this.obterValor(dados, ['cpf', 'CPF'])),
                diretorEmail: this.valorTexto(this.obterValor(dados, ['emailDiretorTecnico', 'EMAIL_DIRETOR_TECNICO'])),
                empresaEmail: this.valorTexto(this.obterValor(dados, ['emailPrestador', 'EMAIL_PRESTADOR'])),

                razaoSocial: this.valorTexto(this.obterValor(dados, ['razaoSocialPrestador', 'RAZAO_SOCIAL_PRESTADOR'])),
                cep: this.valorTexto(this.obterValor(dados, ['cep', 'CEP'])),
                nomeFantasia: this.valorTexto(this.obterValor(dados, ['nomeFantasia', 'NOME_FANTASIA'])),
                logradouro: this.valorTexto(this.obterValor(dados, ['logradouro', 'LOGRADOURO'])),
                numero: this.valorTexto(this.obterValor(dados, ['numero', 'NUMERO'])),
                bairro: this.valorTexto(this.obterValor(dados, ['bairro', 'BAIRRO'])),
                complemento: this.valorTexto(this.obterValor(dados, ['complemento', 'COMPLEMENTO'])),
                cidade: this.valorTexto(this.obterValor(dados, ['cidade', 'CIDADE'])),
                estado: this.valorTexto(this.obterValor(dados, ['estado', 'ESTADO'])),
                cnpj: this.valorTexto(this.obterValor(dados, ['cnpj', 'CNPJ'])),
                ddd: this.valorTexto(this.obterValor(dados, ['ddd', 'DDD'])),
                telefone: this.valorTexto(this.obterValor(dados, ['telefone', 'TELEFONE'])),
                classificacao: classificacaoLabel,
                instalacoes: instalacao,

                funcionamentoNaturezaTipo: naturezaTipo,
                funcionamentoNaturezaEsfera: naturezaEsfera,
                funcionamentoModalidade: modalidade,
                funcionamentoCategoria: categoria,
                funcionamentoTipoOrganizacao: tipoOrganizacao,
                funcionamentoCapitalSocial: this.valorTexto(this.obterValor(dados, ['capitalSocial', 'CAPITAL_SOCIAL'])),
                funcionamentoCrmMatriz: this.valorTexto(this.obterValor(dados, ['crmMatriz', 'CRM_MATRIZ'])),
                funcionamentoUfMatriz: this.valorTexto(this.obterValor(dados, ['ufMatriz', 'UF_MATRIZ'])),
                funcionamentoCnpjMatriz: this.valorTexto(this.obterValor(dados, ['cnpjMatriz', 'CNPJ_MATRIZ'])),
                funcionamentoRazaoSocialMatriz: this.valorTexto(this.obterValor(dados, ['razaoSocialMatriz', 'RAZAO_SOCIAL_MATRIZ'])),
                funcionamentoRazaoSocialMantenedora: this.valorTexto(this.obterValor(dados, ['razaoSocialMantenedora', 'RAZAO_SOCIAL_MANTENEDORA'])),
                funcionamentoNaturezaMantenedoraTipo: naturezaMantenedora,

                docLocalRegistro: localRegistro,
                docDocumentoConstituicao: isJuntaComercial ? numeroNire : tipoDocumentoDescricao,
                docNumeroRegistroDocumento: isJuntaComercial ? dataDocumentoConstituicao : numeroRegistroDocumento,
                docNumeroNire: numeroNire,
                docData: dataDocumentoConstituicao,
                docNumeroLivro: this.valorTexto(this.obterValor(dados, ['numeroLivro', 'NUMERO_LIVRO', 'NU_LIVRO_DOC_CONSTITUICAO'])),
                docNumeroFolha: this.valorTexto(this.obterValor(dados, ['numeroFolha', 'NUMERO_FOLHA', 'NU_FOLHA_DOC_CONSTITUICAO'])),
                docLicencaSanitaria: licencaSanitaria,
                docValidade: this.formatarData(this.valorTexto(this.obterValor(dados, ['dataValidadeLicencaSanitaria', 'DATA_VALIDADE_LICENCA_SANITARIA']))),
                docNumeroAlvara: this.valorTexto(this.obterValor(dados, ['numeroAlvaraFuncionamento', 'NUMERO_ALVARA_FUNCIONAMENTO'])),
                docDataAlvara: this.formatarData(this.valorTexto(this.obterValor(dados, ['dataValidadeAlvara', 'DATA_VALIDADE_ALVARA']))),
                docNumeroCnes: this.valorTexto(this.obterValor(dados, ['numeroCNES', 'numeroCnes', 'NUMERO_CNES'])),
                docEnsino: this.normalizarSimNao(this.valorTexto(this.obterValor(dados, ['prestaServicosInstituicaoEnsino', 'IN_ENSINO']))),
                docGestao: this.obterGestao(tipoGestao),
                docTipoGestao: tipoGestao,
                docAbrangenciaServico: abrangenciaServico,
                docCondicaoFuncionamento: condicaoFuncionamento
            },
            { emitEvent: false }
        );
        this.form.disable({ emitEvent: false });
        this.editando.set(false);
        this.jsonAtualizacao.set('');
    }

    private montarRequest(): PrePrestadorRequest | null {
        const numeroDireto = this.protocoloSelecionado()?.numeroProtocolo?.trim();
        const anoDireto = this.protocoloSelecionado()?.anoProtocolo?.trim();
        const protocoloDireto = numeroDireto && anoDireto ? ([numeroDireto, anoDireto] as [string, string]) : null;

        const portal = this.protocoloSelecionado()?.portal?.trim();
        const sas = this.protocoloSelecionado()?.sas?.trim();
        const protocolo = protocoloDireto ?? this.extrairNumeroAno(portal) ?? this.extrairNumeroAno(sas);
        const ufCrmDireto = this.protocoloSelecionado()?.ufCrm?.trim().toUpperCase();
        const ufCrm = ufCrmDireto || this.extrairUfCrm(this.codigoEstado());

        if (!protocolo || !ufCrm) {
            return null;
        }

        const [numeroProtocolo, anoProtocolo] = protocolo;

        return { numeroProtocolo, anoProtocolo, ufCrm };
    }

    private extrairNumeroAno(valor?: string): [string, string] | null {
        if (!valor) {
            return null;
        }

        const match = valor.match(/(\d+)\s*\/\s*(\d{4})/);

        if (!match) {
            return null;
        }

        const numeroProtocolo = match[1]?.trim();
        const anoProtocolo = match[2]?.trim();

        if (!numeroProtocolo || !anoProtocolo) {
            return null;
        }

        return [numeroProtocolo, anoProtocolo];
    }

    private extrairUfCrm(valor: string): string {
        const texto = valor?.trim().toUpperCase();

        if (!texto) {
            return '';
        }

        const match = texto.match(/([A-Z]{2})$/);
        return match?.[1] ?? '';
    }

    habilitarEdicao(): void {
        // Guarda a mesma regra que apaga o item no menu: `disabled` é aparência, e chamada por outro
        // caminho (atalho, teste, código futuro) abriria a edição numa aba que não tem o que salvar.
        if (!this.abaEditavel()) {
            return;
        }

        this.editando.set(true);
        this.jsonAtualizacao.set('');
        this.form.enable({ emitEvent: false });
    }

    cancelarEdicao(): void {
        this.editando.set(false);
        this.jsonAtualizacao.set('');
        this.form.disable({ emitEvent: false });
        this.ultimaChaveConsulta = '';
        this.carregarPrePrestador();
    }

    salvarEdicao(): void {
        const dto = this.montarObjetoAtualizacao();
        this.jsonAtualizacao.set(JSON.stringify(dto, null, 2));

        this.prePrestadorServicosService
            .atualizarCadastro(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Cadastro atualizado.' });
                    this.editando.set(false);
                    this.form.disable({ emitEvent: false });
                    this.dadosOriginais = dto;
                }
            });
    }

    private montarObjetoAtualizacao(): PrePrestadorDTO {
        const valores = this.form.getRawValue();
        const protocolo = this.montarRequest();
        const isJuntaComercial = this.ehJuntaComercial(valores.docLocalRegistro);

        return {
            ...(this.dadosOriginais ?? {}),
            numeroProtocolo: protocolo?.numeroProtocolo ?? this.dadosOriginais?.numeroProtocolo,
            anoProtocolo: protocolo?.anoProtocolo ?? this.dadosOriginais?.anoProtocolo,
            ufCrmDt: protocolo?.ufCrm ?? valores.diretorUf ?? this.dadosOriginais?.ufCrmDt,
            nome: valores.diretorNome,
            numeroCrmDt: this.numeroOuOriginal(valores.diretorCrm, this.dadosOriginais?.numeroCrmDt),
            cpf: valores.diretorCpf,
            emailDiretorTecnico: valores.diretorEmail,
            emailPrestador: valores.empresaEmail,
            razaoSocialPrestador: valores.razaoSocial,
            nomeFantasia: valores.nomeFantasia,
            cnpj: valores.cnpj,
            classificacao: this.atualizarDescricao(this.dadosOriginais?.classificacao, valores.classificacao),
            tipoInstalacao: this.atualizarDescricao(this.dadosOriginais?.tipoInstalacao, valores.instalacoes),
            cep: valores.cep,
            logradouro: valores.logradouro,
            numero: this.numeroOuOriginal(valores.numero, this.dadosOriginais?.numero),
            bairro: valores.bairro,
            complemento: valores.complemento,
            cidade: valores.cidade,
            estado: valores.estado,
            ddd: valores.ddd,
            telefone: valores.telefone,
            naturezaJuridica: this.numeroOuOriginal(valores.funcionamentoNaturezaTipo, this.dadosOriginais?.naturezaJuridica),
            tipoMantenedora: this.atualizarDescricao(this.dadosOriginais?.tipoMantenedora, valores.funcionamentoNaturezaEsfera),
            modalidadeInscricao: valores.funcionamentoModalidade,
            categoria: valores.funcionamentoCategoria,
            tipoEstabelecimento: valores.funcionamentoTipoOrganizacao,
            capitalSocial: valores.funcionamentoCapitalSocial,
            crmMatriz: valores.funcionamentoCrmMatriz,
            ufMatriz: valores.funcionamentoUfMatriz,
            cnpjMatriz: valores.funcionamentoCnpjMatriz,
            razaoSocialMatriz: valores.funcionamentoRazaoSocialMatriz,
            razaoSocialMantenedora: valores.funcionamentoRazaoSocialMantenedora,
            naturezaJuridicaMantenedora: valores.funcionamentoNaturezaMantenedoraTipo,
            localRegistro: valores.docLocalRegistro,
            numeroNire: isJuntaComercial ? valores.docDocumentoConstituicao : this.dadosOriginais?.numeroNire,
            tipoDocumento: isJuntaComercial ? this.dadosOriginais?.tipoDocumento : this.atualizarDescricao(this.dadosOriginais?.tipoDocumento, valores.docDocumentoConstituicao),
            numeroRegistroDocumento: isJuntaComercial ? this.dadosOriginais?.numeroRegistroDocumento : valores.docNumeroRegistroDocumento,
            numeroLivro: isJuntaComercial ? this.dadosOriginais?.numeroLivro : valores.docNumeroLivro,
            numeroFolha: isJuntaComercial ? this.dadosOriginais?.numeroFolha : valores.docNumeroFolha,
            dataDocumentoConstituicao: isJuntaComercial ? valores.docNumeroRegistroDocumento : valores.docData,
            tipoLicencaSanitaria: valores.docLicencaSanitaria,
            dataValidadeLicencaSanitaria: valores.docValidade,
            numeroAlvaraFuncionamento: valores.docNumeroAlvara,
            dataValidadeAlvara: valores.docDataAlvara,
            numeroCNES: valores.docNumeroCnes,
            prestaServicosInstituicaoEnsino: valores.docEnsino,
            tipoGestao: valores.docTipoGestao,
            abrangenciaServico: valores.docAbrangenciaServico,
            condicaoFuncionamento: valores.docCondicaoFuncionamento,
            corpoSocietario: this.corpoSocietario(),
            corpoClinico: this.corpoClinico(),
            especialidades: this.especialidades(),
            areasAtuacao: this.areasAtuacao(),
            servicos: this.servicosPrestador(),
            atividades: this.atividades(),
            recursosMateriais: this.recursosMateriais(),
            recursosHumanos: this.recursosHumanos()
        };
    }

    private atualizarDescricao<T extends { id?: string; descricao?: string }>(valor: T | null | undefined, descricao: string): T | undefined {
        const texto = this.valorTexto(descricao);
        if (!texto && !valor) {
            return undefined;
        }

        return { ...(valor ?? ({} as T)), id: valor?.id ?? texto, descricao: texto } as T;
    }

    private numeroOuOriginal(valor: unknown, original?: number): number | undefined {
        if (valor === null || valor === undefined || valor === '') {
            return original;
        }

        const numero = Number(valor);
        return Number.isNaN(numero) ? original : numero;
    }

    private obterGestao(tipoGestao?: string): string {
        const normalizado = this.normalizarSimNao(tipoGestao);

        if (normalizado) {
            return normalizado;
        }

        return tipoGestao ? 'S' : '';
    }

    ehJuntaComercialAtual(): boolean {
        return this.ehJuntaComercial(this.form?.get('docLocalRegistro')?.value);
    }

    documentoConstituicaoRotulo(): string {
        return this.ehJuntaComercialAtual() ? 'N\u00famero NIRE' : 'Documento Constitui\u00e7\u00e3o';
    }

    numeroRegistroDocumentoRotulo(): string {
        return this.ehJuntaComercialAtual() ? 'Data' : 'N\u00famero';
    }

    private ehJuntaComercial(valor: unknown): boolean {
        return this.normalizarLocalRegistro(valor) === 'J';
    }

    private normalizarLocalRegistro(valor: unknown): string {
        const texto = this.valorTexto(valor).trim();
        const normalizado = texto
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase();

        if (!normalizado) {
            return '';
        }

        if (normalizado === 'J' || normalizado.includes('JUNTA')) {
            return 'J';
        }

        if (normalizado === 'C' || normalizado.includes('CARTORIO')) {
            return 'C';
        }

        return texto;
    }

    private normalizarSimNao(valor?: string): string {
        const texto = this.valorTexto(valor).trim().toUpperCase();
        const textoSemAcento = texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        if (!texto) {
            return '';
        }

        if (['S', 'SIM', 'TRUE', '1'].includes(textoSemAcento)) {
            return 'S';
        }

        if (['N', 'NAO', 'FALSE', '0'].includes(textoSemAcento)) {
            return 'N';
        }

        return '';
    }

    /**
     * O portal manda os motivos de desconto num campo só, separados por `;` — o legado quebrava em
     * uma linha por motivo (`SplitString(texto, ';')` em `UntVisualizarPrestador.pas:713`). Sem isso
     * a lista sai grudada num parágrafo único e fica ilegível quando há mais de um motivo.
     */
    private normalizarDescontoMotivo(valor: unknown): string {
        return this.valorTexto(valor)
            .replace(/\r\n/g, '\n')
            .split(/[;\n]/)
            .map((motivo) => motivo.trim())
            .filter(Boolean)
            .join('\n');
    }

    anuidadeRotulo(): string {
        return this.isAnuidadePaga() ? 'Anuidade paga' : 'Anuidade não paga';
    }

    private normalizarBoolean(valor: unknown): boolean | null {
        if (typeof valor === 'boolean') {
            return valor;
        }

        if (typeof valor === 'number') {
            if (valor === 1) {
                return true;
            }

            if (valor === 0) {
                return false;
            }
        }

        const texto = this.valorTexto(valor).trim().toUpperCase();
        const textoSemAcento = texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (['S', 'SIM', 'TRUE', '1'].includes(textoSemAcento)) {
            return true;
        }

        if (['N', 'NAO', 'FALSE', '0'].includes(textoSemAcento)) {
            return false;
        }

        return null;
    }

    private formatarData(valor?: string): string {
        const texto = this.valorTexto(valor).trim();

        if (!texto) {
            return '';
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
            return texto;
        }

        const [somenteData] = texto.split('T');
        const partes = somenteData.split('-');

        if (partes.length === 3) {
            const [ano, mes, dia] = partes;

            if (ano && mes && dia) {
                return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
            }
        }

        return texto;
    }

    private valorTexto(valor: unknown): string {
        if (valor === null || valor === undefined) {
            return '';
        }

        return String(valor);
    }

    valorColecao(item: unknown, aliases: string[]): string {
        if (!item || typeof item !== 'object') {
            return '';
        }

        return this.valorTexto(this.obterValor(item as Record<string, unknown>, aliases));
    }

    possuiAnexo(anexo: AnexoPrePrestador): boolean {
        return !!this.valorTexto(anexo.linkAnexo).trim();
    }

    onVisualizarAnexo(event: Event, anexo: AnexoPrePrestador): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(anexo.linkAnexo);
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private atualizarAnexos(response: PrePrestadorDTO): void {
        this.anexosDisponiveis.set(
            this.camposAnexos
                .map((campo) => {
                    const valorNormalizado = this.valorTexto(response?.[campo.chave]).trim();
                    if (!valorNormalizado) {
                        return null;
                    }

                    return {
                        chave: String(campo.chave),
                        titulo: campo.titulo,
                        linkAnexo: valorNormalizado
                    } satisfies AnexoPrePrestador;
                })
                .filter((item): item is AnexoPrePrestador => item !== null)
        );
    }

    private visualizarAnexoPorCampo(campoAnexo: unknown): void {
        const idAnexo = this.extrairIdAnexo(campoAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.prePrestadorServicosService
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const link = this.valorTexto(campoAnexo).trim();
        if (!link) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(link);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(link, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e continua fallback textual.
        }

        if (/^[A-Za-z0-9_-]+$/.test(link)) {
            return link;
        }

        return null;
    }

    private obterValor<T = unknown>(source: Record<string, unknown>, aliases: string[]): T | undefined {
        const indiceNormalizado = new Map<string, unknown>();

        for (const [chave, valor] of Object.entries(source)) {
            indiceNormalizado.set(this.normalizarChave(chave), valor);
        }

        for (const alias of aliases) {
            const valor = alias.includes('.') ? this.obterValorPorCaminho(source, alias) : (source[alias] ?? indiceNormalizado.get(this.normalizarChave(alias)));

            if (valor !== undefined) {
                return valor as T;
            }
        }

        return undefined;
    }

    private obterValorPorCaminho(source: Record<string, unknown>, path: string): unknown {
        const partes = path.split('.');
        let atual: unknown = source;

        for (const parte of partes) {
            if (!atual || typeof atual !== 'object') {
                return undefined;
            }

            const objeto = atual as Record<string, unknown>;
            const chave = Object.keys(objeto).find((item) => this.normalizarChave(item) === this.normalizarChave(parte));

            if (!chave) {
                return undefined;
            }

            atual = objeto[chave];
        }

        return atual;
    }

    private normalizarChave(chave: string): string {
        return this.valorTexto(chave)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]/g, '')
            .toLowerCase();
    }

    private adicionarOpcao(opcoes: OpcaoSelecao[], value: string, label?: string): void {
        const valor = value.trim();

        if (!valor || opcoes.some((opcao) => opcao.value === valor)) {
            return;
        }

        opcoes.push({
            label: label?.trim() || valor,
            value: valor
        });
    }

    private resetarOpcoesDinamicas(): void {
        this.instalacoesOptions = [
            { label: 'Pr\u00f3prias', value: 'P' },
            { label: 'Alugadas', value: 'A' },
            { label: 'Cedidas', value: 'C' },
            { label: 'ADM', value: 'D' }
        ];
        this.naturezaTipoOptions = [
            { label: 'P\u00fablico', value: 'P' },
            { label: 'Privado', value: 'R' }
        ];
        this.naturezaEsferaOptions = [
            { label: 'P\u00fablico', value: 'U' },
            { label: 'Privado', value: 'R' }
        ];
    }

    private normalizarLista<T>(lista?: T[]): T[] {
        return (lista ?? []).filter((item) => Boolean(item));
    }

    private sincronizarAbaExterna(): void {
        const aba = this.abaSelecionadaExterna();

        if (!aba) {
            return;
        }

        if (this.abas.some((item) => item.value === aba)) {
            this.abaSelecionada.set(aba);

            if (this.editando() && aba !== EAbaPrePrestador.DADOS && aba !== EAbaPrePrestador.FUNC && aba !== EAbaPrePrestador.DOC) {
                this.cancelarEdicao();
            }
        }
    }

    private resetarColecoes(): void {
        this.corpoSocietario.set([]);
        this.corpoClinico.set([]);
        this.especialidades.set([]);
        this.areasAtuacao.set([]);
        this.servicosPrestador.set([]);
        this.atividades.set([]);
        this.recursosMateriais.set([]);
        this.recursosHumanos.set([]);
    }

    private obterValoresIniciaisFormulario() {
        return {
            diretorNome: '',
            diretorCrm: '',
            diretorUf: '',
            diretorCpf: '',
            diretorEmail: '',
            empresaEmail: '',

            razaoSocial: '',
            cep: '',
            nomeFantasia: '',
            logradouro: '',
            numero: '',
            bairro: '',
            complemento: '',
            cidade: '',
            estado: '',
            cnpj: '',
            ddd: '',
            telefone: '',
            classificacao: '',
            instalacoes: '',

            funcionamentoNaturezaTipo: '',
            funcionamentoNaturezaEsfera: '',
            funcionamentoModalidade: '',
            funcionamentoCategoria: '',
            funcionamentoTipoOrganizacao: '',
            funcionamentoCapitalSocial: '',
            funcionamentoCrmMatriz: '',
            funcionamentoUfMatriz: '',
            funcionamentoCnpjMatriz: '',
            funcionamentoRazaoSocialMatriz: '',
            funcionamentoRazaoSocialMantenedora: '',
            funcionamentoNaturezaMantenedoraTipo: '',

            docLocalRegistro: '',
            docDocumentoConstituicao: '',
            docNumeroRegistroDocumento: '',
            docNumeroNire: '',
            docData: '',
            docNumeroLivro: '',
            docNumeroFolha: '',
            docLicencaSanitaria: '',
            docValidade: '',
            docNumeroAlvara: '',
            docDataAlvara: '',
            docNumeroCnes: '',
            docEnsino: '',
            docGestao: '',
            docTipoGestao: '',
            docAbrangenciaServico: '',
            docCondicaoFuncionamento: ''
        };
    }
}
